/**
 * IterationsTimeline — vertical timeline of training-session iterations.
 *
 * Each iteration is a row with kind badge, author, timestamp, content
 * preview (line-clamped) and an "approved" pill when applicable.
 * Selecting a row exposes a side-by-side diff against the previous
 * iteration via the parent-passed callback.
 */
import type { TrainingIteration } from '@chaos/types/learning';
import { Bot, Check, MessageSquare, Pen, UserCheck } from 'lucide-react';

import { cn } from '../../lib/utils';
import { fmtRelative } from './formatters';

interface IterationsTimelineProps {
  iterations: TrainingIteration[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  className?: string;
}

const KIND_ICON = {
  agent_draft: Bot,
  tuner_correction: Pen,
  tuner_note: MessageSquare,
  final: UserCheck,
} as const;

const KIND_LABEL = {
  agent_draft: 'Agent draft',
  tuner_correction: 'Tuner correction',
  tuner_note: 'Tuner note',
  final: 'Final',
} as const;

export function IterationsTimeline({
  iterations,
  selectedId,
  onSelect,
  className,
}: IterationsTimelineProps) {
  if (iterations.length === 0) {
    return (
      <div className={cn('px-3 py-6 text-center text-[12px] text-muted-foreground/70', className)}>
        No iterations yet.
      </div>
    );
  }
  return (
    <ol className={cn('flex flex-col gap-2', className)}>
      {iterations.map((it) => {
        const Icon = KIND_ICON[it.kind] ?? Bot;
        const label = KIND_LABEL[it.kind] ?? it.kind;
        const isSelected = selectedId === it.id;
        return (
          <li key={it.id}>
            <button
              type="button"
              onClick={() => onSelect(it.id)}
              className={cn(
                'group/iter flex w-full items-start gap-2 rounded border px-3 py-2 text-left transition-colors',
                isSelected
                  ? 'border-foreground/30 bg-muted/60'
                  : 'border-border bg-background hover:bg-muted/40',
              )}
              aria-pressed={isSelected}
            >
              <span
                aria-hidden
                className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded text-muted-foreground"
              >
                <Icon className="h-3.5 w-3.5" />
              </span>
              <div className="flex min-w-0 flex-1 flex-col">
                <div className="flex items-center gap-2 text-[12px]">
                  <span className="font-semibold text-foreground">
                    #{it.index + 1} {label}
                  </span>
                  {it.approved ? (
                    <span className="inline-flex items-center gap-1 rounded border border-border bg-muted/40 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-success">
                      <Check className="h-2.5 w-2.5" aria-hidden /> Approved
                    </span>
                  ) : null}
                </div>
                <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
                  <span>{it.authorName ?? it.authorKind}</span>
                  <span aria-hidden>·</span>
                  <span>{fmtRelative(it.createdAt)}</span>
                </div>
                <p className="mt-1 line-clamp-2 whitespace-pre-wrap font-mono text-[11px] text-foreground/80">
                  {it.content}
                </p>
              </div>
            </button>
          </li>
        );
      })}
    </ol>
  );
}

export { KIND_LABEL };
